import React, { useEffect, useMemo, useState } from "react";
import { Box, Paper, Grid, TextField, Button, CircularProgress, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import Sidebar from "../Sidebar/Sidebar";
import useAuth from "../../hooks/useAuth";
import { useTranslation } from "react-i18next";

import { listTenants } from "../../services/tenantSuperadminService";
import TenantModal from "./TenantModal";
import TenantRow from "./TenantRow"; // ✅ standalone row component

export default function SuperAdminTenantsPage() {
  const { isLoggedIn, user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [tenants, setTenants] = useState([]);
  const [search, setSearch] = useState("");

  // one modal: tenantId=null => create, tenantId=number => edit
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTenantId, setModalTenantId] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const data = await listTenants();
      const list = Array.isArray(data) ? data : data?.results || [];
      setTenants(list);
    } catch (e) {
      enqueueSnackbar(t("superAdminTenantsPage.snackbar.failedToLoadTenants", "Nepodarilo sa načítať orgnaizácie."), {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isLoggedIn) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tenants;
    return tenants.filter((x) => {
      return (
        (x.name || "").toLowerCase().includes(q) ||
        (x.slug || "").toLowerCase().includes(q) ||
        String(x.id).includes(q)
      );
    });
  }, [tenants, search]);

  const openCreate = () => {
    setModalTenantId(null);
    setModalOpen(true);
  };

  const openDetail = (id) => {
    setModalTenantId(id);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalTenantId(null); // ✅ reset selection
  };

  if (!isLoggedIn) return null;

  return (
    <>
      <Sidebar user={user} />

      <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
        <Box sx={{ width: "100%", maxWidth: 1100 }}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} sm={6}>
                <Typography variant="h6">{t("superAdminTenantsPage.title", "Organizácie")}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("superAdminTenantsPage.subtitle", "Správa organizácií")}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6} sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button variant="contained" onClick={openCreate}>
                  {t("superAdminTenantsPage.actions.createTenant", "Vytvoriť organizáciu")}
                </Button>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("superAdminTenantsPage.search.label", "Hľadať")}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </Grid>
            </Grid>
          </Paper>

          {loading ? (
            <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
              <CircularProgress />
            </Box>
          ) : filtered.length === 0 ? (
            <Paper sx={{ p: 2 }}>
              <Typography>{t("superAdminTenantsPage.empty.noTenantsFound", "Nenašli sa žiadne organizácie.")}</Typography>
            </Paper>
          ) : (
            <Grid container spacing={1}>
              {filtered.map((x) => (
                <Grid item xs={12} key={x.id}>
                  <TenantRow tenant={x} onOpen={openDetail} />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Box>

      <TenantModal
        open={modalOpen}
        tenantId={modalTenantId}
        onClose={closeModal}
        onChanged={refresh} // refresh list after create/update/delete
      />
    </>
  );
}
